import { createFormHookContexts, createFormHook } from '@tanstack/react-form'
import { Input2, InputProps2, Textarea, TextareaProps } from '../../components/ui'
import { RefObject, useCallback, useMemo, useRef } from 'react'
import { isEmail, isURL } from 'validator'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib'

export const { fieldContext, formContext, useFieldContext } = createFormHookContexts()

export const useScrollToFirstInvalidField = ({ ref }: { ref: RefObject<HTMLElement> }) => {
  return {
    scrollToFirstInvalidField: useCallback(() => {
      const firstInvalidElement = ref.current?.querySelector('[data-valid="false"]')
      if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }, [ref]),
  }
}

export const FormFieldFooter = ({
  helpText,
  showCount,
  maxCount,
  value,
  meta,
}: {
  helpText?: string
  showCount?: boolean
  maxCount?: number
  value?: string
  meta: {
    isTouched: boolean
    errors: string[]
  }
}) => {
  const counter = useMemo(() => {
    const length = value?.length ?? 0
    if (maxCount) {
      return `${length}/${maxCount}`
    }
    return length
  }, [value, maxCount])

  const errors = useMemo(() => {
    return meta.isTouched && meta.errors.length > 0 ? meta.errors : null
  }, [meta.errors, meta.isTouched])

  return (
    <div
      className={cn('flex justify-end', {
        'justify-between': errors || helpText,
      })}
    >
      {helpText && !errors && <span className='text-grey-s4 text-xs'>{helpText}</span>}
      {errors && <span className='text-xs text-warning-s5'>{errors.join(', ')}</span>}
      {showCount && <span className='text-grey-s4 text-xs'>{counter}</span>}
    </div>
  )
}

export function InputField({
  required,
  className,
  showCount,
  maxCount,
  helpText,
  inputClassName,
  ...props
}: Omit<InputProps2, 'value' | 'variant' | 'onBlur' | 'onChange'> & {
  helpText?: string
  showCount?: boolean
  maxCount?: number
  inputClassName?: string
}) {
  const field = useFieldContext<string>()
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Input2
        ref={inputRef}
        variant={
          field.state.meta.isTouched && field.state.meta.errors.length > 0 ? 'error' : 'default'
        }
        value={field.state.value ?? ''}
        onChange={(e) => {
          field.handleChange(e.target.value)
        }}
        onBlur={field.handleBlur}
        inputClassName={inputClassName}
        data-valid={field.state.meta.errors.length === 0}
        {...props}
      />
      <FormFieldFooter
        helpText={helpText}
        showCount={showCount}
        maxCount={maxCount}
        value={field.state.value}
        meta={field.state.meta}
      />
    </div>
  )
}

export function TextAreaField({
  showCount,
  maxCount,
  helpText,
  inputClassName,
  ...props
}: Omit<TextareaProps, 'value' | 'onBlur' | 'onChange'> & {
  inputClassName?: string
  helpText?: string
  showCount?: boolean
  maxCount?: number
}) {
  const field = useFieldContext<string>()

  return (
    <div className='flex flex-col gap-2'>
      <Textarea
        value={field.state.value ?? ''}
        variant={
          field.state.meta.isTouched && field.state.meta.errors.length > 0 ? 'error' : 'default'
        }
        onChange={(e) => {
          field.handleChange(e.target.value)
        }}
        onBlur={field.handleBlur}
        className={inputClassName}
        data-valid={field.state.meta.errors.length === 0}
        {...props}
      />
      <FormFieldFooter
        helpText={helpText}
        showCount={showCount}
        maxCount={maxCount}
        value={field.state.value}
        meta={field.state.meta}
      />
    </div>
  )
}

export const { useAppForm, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    InputField,
    TextAreaField,
  },
  formComponents: {},
})

export type TFormValidator<T extends string | string[] | number | any[]> = (fieldValue: {
  value: T
}) => string | undefined

/**
 * Custom hook that provides form validators
 * This allows us to use hooks inside validator functions in the future
 */
export const useFormValidators = () => {
  const { t } = useTranslation()

  const required = useCallback(
    (required: boolean): TFormValidator<string | any[]> => {
      return ({ value }: { value: string | any[] }): string | undefined => {
        if (!required) {
          return undefined
        }
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return t('components.Form.fieldRequired', {
              defaultValue: 'This field is required',
            })
          }
          return undefined
        }
        if (!value || value.trim() === '') {
          return t('components.Form.fieldRequired', {
            defaultValue: 'This field is required',
          })
        }
        return undefined
      }
    },
    [t],
  )

  /**
   * Min length validator factory
   */
  const minLength = useCallback(
    (min: number): TFormValidator<string | any[]> => {
      return ({ value }: { value: string | any[] }): string | undefined => {
        if (Array.isArray(value) && value.length < min) {
          return t('components.Form.minOptionsRequired', {
            defaultValue: 'Min. {{min}} options',
            min,
          })
        }
        if (value.length < min) {
          return t('components.Form.minCharactersRequired', {
            defaultValue: 'Min. {{min}} characters',
            min,
          })
        }
        return undefined
      }
    },
    [t],
  )

  /**
   * Max length validator factory
   */
  const maxLength = useCallback(
    (max: number): TFormValidator<string | any[]> => {
      return ({ value }: { value: string | any[] }): string | undefined => {
        if (Array.isArray(value) && value.length > max) {
          return t('components.Form.maxOptionsAllowed', {
            defaultValue: 'Max. {{max}} options',
            max,
          })
        }

        if (value.length > max) {
          return t('components.Form.maxCharactersAllowed', {
            defaultValue: 'Max. {{max}} characters',
            max,
          })
        }
        return undefined
      }
    },
    [t],
  )

  /**
   * Email validator factory
   */
  const email = useCallback((): TFormValidator<string | any[]> => {
    return ({ value }: { value: string | any[] }): string | undefined => {
      if (Array.isArray(value) || !isEmail(value)) {
        return t('components.Form.invalidEmail', {
          defaultValue: 'Must be a valid email address',
        })
      }
      return undefined
    }
  }, [t])

  /**
   * URL validator factory
   */
  const url = useCallback((): TFormValidator<string | any[]> => {
    return ({ value }: { value: string | any[] }): string | undefined => {
      if (Array.isArray(value) || !isURL(value, { protocols: ['http', 'https'] })) {
        return t('components.Form.invalidUrl', {
          defaultValue: 'Must be a valid URL',
        })
      }
      return undefined
    }
  }, [t])

  const min = useCallback((min: number): TFormValidator<number> => {
    return ({ value }: { value: number }): string | undefined => {
      if (Number(value) < min) {
        return `Min. ${min}`
      }
      return undefined
    }
  }, [])

  /**
   * Composes multiple validators into a single validator
   * Returns the first error found or undefined if all pass
   */
  const composeValidators = useCallback(
    <T extends string | any[] | number>(...validators: TFormValidator<T>[]): TFormValidator<T> => {
      return (fieldValue: { value: T }): string | undefined => {
        for (const validator of validators) {
          const error = validator(fieldValue)
          if (error) {
            return error
          }
        }
        return undefined
      }
    },
    [],
  )

  return {
    required,
    minLength,
    maxLength,
    email,
    url,
    min,
    composeValidators,
  }
}
